import { createSelector } from 'reselect';
import { nest } from 'd3';
import idx from 'idx';

const householdIncomeSelector = (state) => state.householdIncome;
const householdMembersAbove14Selector = (state) =>
  state.householdMembersAbove14;
const householdMembersBelow14Selector = (state) =>
  state.householdMembersBelow14;
const distributionDataSelector = (state) => state.distributionData;
const compareDataSelector = (state) => state.compareData;
const overallKeySelector = (state) => state.overallKey;
const compareKeySelector = (state) => state.compareKey;
const additionalDataSelector = (state) => state.additionalData;
const tooltipKeySelector = (state) => state.tooltipKey;

function getGroup(id) {
  return idx(id, (_) => _.match(/^.*\./g)[0].replace('.', ''));
}

function getLabel(id) {
  return id.replace(/^.*\./g, '');
}

function getAboveValue(data, key, income) {
  if (!data) {
    return 0;
  }

  const foundData = data.slice(0).find((d) => income <= d[key]);

  if (!foundData) {
    return 0;
  }

  return foundData['Anteil der reicheren Bevölkerung'];
}

const columnOrder = [
  'Haushalt',
  'Geschlecht',
  'Erwerbsstatus',
  'Bildung',
  'Alter',
  'Wohnsituation',
  'Herkunft',
  'Einwohnergroeße',
];

function groupOptions(options) {
  return nest()
    .key((d) => d.group)
    .sortKeys((a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b))
    .entries(options)
    .reduce((res, curr) => {
      if (curr.key === 'null') {
        res = curr.values.concat(res);
      } else {
        res.push({
          label: curr.key,
          options: curr.values,
        });
      }
      return res;
    }, []);
}

export const weightedIncomeSelector = createSelector(
  [
    householdIncomeSelector,
    householdMembersAbove14Selector,
    householdMembersBelow14Selector,
  ],
  (householdIncome, householdMembersAbove14, householdMembersBelow14) =>
    Math.round(
      householdIncome /
        (1 +
          (householdMembersAbove14 - 1) * 0.5 +
          householdMembersBelow14 * 0.3)
    )
);

export const optionsSelector = createSelector(
  [distributionDataSelector],
  (distributionData) => {
    if (!distributionData) {
      return [];
    }

    const options = distributionData.columns
      .filter((d) => d !== '' && d !== 'Einkommensgruppe')
      .map((d) => ({ label: getLabel(d), value: d, group: getGroup(d) }));

    return options;
  }
);

export const compareOptionSelector = createSelector(
  [optionsSelector, compareKeySelector],
  (options, compareKey) => options.find((d) => d.value === compareKey)
);

export const overallOptionSelector = createSelector(
  [optionsSelector, overallKeySelector],
  (options, overallKey) => options.find((d) => d.value === overallKey)
);

export const groupedOptionsSelector = createSelector(
  [optionsSelector],
  (options) => groupOptions(options)
);

export const overallAboveSelector = createSelector(
  [compareDataSelector, overallKeySelector, weightedIncomeSelector],
  (compareData, overallKey, weightedIncome) => {
    if (weightedIncome >= 17000) {
      return 0;
    }

    return getAboveValue(compareData, overallKey, weightedIncome);
  }
);

export const compareAboveSelector = createSelector(
  [compareDataSelector, compareKeySelector, weightedIncomeSelector],
  (compareData, compareKey, weightedIncome) => {
    if (weightedIncome >= 17000) {
      return 0;
    }

    return getAboveValue(compareData, compareKey, weightedIncome);
  }
);

export const tooltipDataSelector = createSelector(
  [additionalDataSelector, tooltipKeySelector],
  (additionalData, tooltipKey) => {
    const key = tooltipKey.replace('Maenner', 'Männer');

    if (!additionalData) {
      return false;
    }

    return {
      ...additionalData[key],
      label: getLabel(key),
    };
  }
);

export default {
  weightedIncomeSelector,
  optionsSelector,
  overallAboveSelector,
  compareAboveSelector,
  tooltipDataSelector,
};
